/**
 * Mirrors Wagtail admin content metrics (see wagtail/client/src/includes/contentMetrics.ts).
 * Word count uses Intl.Segmenter; reading time uses language-specific WPM with English fallback.
 */

/**
 * Language-specific reading speeds (words per minute) from the same meta-analysis Wagtail cites.
 * @see https://osf.io/preprints/psyarxiv/xynwg/
 * @see https://doi.org/10.1016/j.jml.2019.104047
 */
export const READING_SPEEDS_WPM = {
  ar: 181,
  zh: 260,
  nl: 228,
  en: 238,
  fi: 195,
  fr: 214,
  de: 260,
  he: 224,
  it: 285,
  ko: 226,
  es: 278,
  sv: 218,
} as const;

function getWordCountWithSegmenter(lang: string, text: string): number {
  const segmenter = new Intl.Segmenter(lang, { granularity: 'word' });
  let count = 0;
  for (const { isWordLike } of segmenter.segment(text)) {
    if (isWordLike) count += 1;
  }
  return count;
}

/** Approximate word count when Intl.Segmenter is unavailable (matches no production browser target). */
function getWordCountFallback(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function getWordCount(lang: string, text: string): number {
  if (!text.trim()) return 0;
  if (typeof Intl.Segmenter === 'undefined') {
    return getWordCountFallback(text);
  }
  return getWordCountWithSegmenter(lang, text);
}

export function getReadingTime(lang: string, wordCount: number): number {
  const locale = lang.split('-')[0] ?? 'en';
  const readingSpeed =
    locale in READING_SPEEDS_WPM
      ? READING_SPEEDS_WPM[locale as keyof typeof READING_SPEEDS_WPM]
      : READING_SPEEDS_WPM.en;
  return Math.round(wordCount / readingSpeed);
}
